import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { detectInAppBrowser, openInSystemBrowser } from "../lib/detectInAppBrowser";
import { X, Mail, Lock, User as UserIcon, Phone, MapPin, AlertCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Klaida";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AuthModal() {
  const { authModalOpen, closeAuthModal, loginWithGoogle, loginWithPassword, register } = useAuth();
  const { t } = useI18n();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);

  if (!authModalOpen) return null;

  // Detect Facebook / Messenger / Instagram / TikTok in-app browsers.
  // Google OAuth (`disallowed_useragent` error 403) won't work inside them.
  const { isInApp } = detectInAppBrowser();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await loginWithPassword(email, password);
        toast.success("Prisijungta!");
      } else {
        if (!name.trim()) { toast.error("Vardas privalomas"); setBusy(false); return; }
        if (password.length < 6) { toast.error("Slaptažodis bent 6 simbolių"); setBusy(false); return; }
        await register({ email, password, name, phone, address });
        toast.success("Sveiki atvykę!");
      }
      closeAuthModal();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="auth-modal">
      <div className="absolute inset-0 bg-black/60" onClick={closeAuthModal} />
      <div className="relative bg-[#FAF9F6] border-2 border-[#1A1A18] rounded-md w-full max-w-md hard-shadow overflow-hidden">
        <button
          data-testid="auth-modal-close"
          onClick={closeAuthModal}
          className="absolute top-3 right-3 p-1.5 rounded hover:bg-[#F0EFEB] z-10"
        >
          <X className="w-5 h-5"/>
        </button>

        <div className="p-6 sm:p-8">
          <h2 className="font-heading font-black text-2xl text-[#1A1A18] mb-1">
            {mode === "login" ? "Prisijungti" : "Sukurti paskyrą"}
          </h2>
          <p className="text-sm text-[#7A7A73] mb-5">
            {mode === "login" ? "Prisijunk prie StopChlamas." : "Visi skelbimai – nemokami."}
          </p>

          {/* In-app browser warning — Google OAuth is blocked here. */}
          {isInApp && (
            <div
              data-testid="inapp-warning"
              className="mb-4 p-3 bg-[#FFF6E5] border border-[#DDA74F] rounded-md text-sm"
            >
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 text-[#C85A32] flex-shrink-0" />
                <div>
                  <p className="font-heading font-bold text-[#1A1A18]">
                    {t("inapp_warning_title")}
                  </p>
                  <p className="mt-1 text-[#4A4A45]">{t("inapp_warning_body")}</p>
                  <button
                    type="button"
                    data-testid="open-in-browser-button"
                    onClick={() => openInSystemBrowser(window.location.href)}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1A18] text-white text-xs font-semibold rounded-md hover:bg-[#C85A32] transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {t("open_in_browser")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Google button */}
          <button
            type="button"
            data-testid="modal-google-button"
            onClick={loginWithGoogle}
            disabled={isInApp}
            className={`w-full flex items-center justify-center gap-3 py-2.5 border-2 rounded-md transition-colors text-sm font-semibold mb-4 ${
              isInApp
                ? "border-[#D1CFC7] text-[#7A7A73] bg-[#F0EFEB] cursor-not-allowed"
                : "border-[#D1CFC7] hover:bg-white hover:border-[#1A1A18]"
            }`}
          >
            <svg className="w-5 h-5" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
            Tęsti su Google
          </button>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-[#D1CFC7]"/>
            <span className="text-xs uppercase tracking-widest text-[#7A7A73]">arba</span>
            <div className="flex-1 h-px bg-[#D1CFC7]"/>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="relative">
                <UserIcon className="w-4 h-4 absolute left-3 top-3.5 text-[#7A7A73]"/>
                <input
                  data-testid="modal-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Vardas"
                  className="w-full bg-white border border-[#D1CFC7] rounded-md pl-9 pr-4 py-3 outline-none focus:ring-2 focus:ring-[#C85A32]"
                  required
                />
              </div>
            )}
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-3.5 text-[#7A7A73]"/>
              <input
                data-testid="modal-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="el.pastas@example.lt"
                className="w-full bg-white border border-[#D1CFC7] rounded-md pl-9 pr-4 py-3 outline-none focus:ring-2 focus:ring-[#C85A32]"
                required
              />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-3.5 text-[#7A7A73]"/>
              <input
                data-testid="modal-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "register" ? "Slaptažodis (bent 6 simb.)" : "Slaptažodis"}
                className="w-full bg-white border border-[#D1CFC7] rounded-md pl-9 pr-4 py-3 outline-none focus:ring-2 focus:ring-[#C85A32]"
                required
                minLength={mode === "register" ? 6 : undefined}
              />
            </div>
            {mode === "register" && (
              <>
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-3 top-3.5 text-[#7A7A73]"/>
                  <input
                    data-testid="modal-phone-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Telefonas (neprivaloma)"
                    className="w-full bg-white border border-[#D1CFC7] rounded-md pl-9 pr-4 py-3 outline-none focus:ring-2 focus:ring-[#C85A32]"
                  />
                </div>
                <div className="relative">
                  <MapPin className="w-4 h-4 absolute left-3 top-3.5 text-[#7A7A73]"/>
                  <input
                    data-testid="modal-address-input"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Adresas / miestas (neprivaloma)"
                    className="w-full bg-white border border-[#D1CFC7] rounded-md pl-9 pr-4 py-3 outline-none focus:ring-2 focus:ring-[#C85A32]"
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              data-testid="modal-submit-button"
              disabled={busy}
              className="w-full btn-primary disabled:opacity-50"
            >
              {busy ? "..." : mode === "login" ? "Prisijungti" : "Registruotis"}
            </button>

            {mode === "login" && (
              <Link
                to="/forgot-password"
                onClick={closeAuthModal}
                data-testid="modal-forgot-password-link"
                className="block text-center text-sm text-[#7A7A73] hover:text-[#C85A32] mt-2"
              >
                {t("forgot_password")}
              </Link>
            )}
          </form>

          <p className="mt-4 text-center text-sm text-[#4A4A45]">
            {mode === "login" ? "Neturi paskyros?" : "Jau turi paskyrą?"}{" "}
            <button
              type="button"
              data-testid="modal-toggle-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#C85A32] font-semibold hover:underline"
            >
              {mode === "login" ? "Registruokis" : "Prisijunk"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
